import "@core/i18n/dayjs-requirements";
import dayjs, { Dayjs } from "dayjs";

const BACKEND_DATE_FORMAT = "YYYY-MM-DD";
export const WEEK_LENGTH = 7;
export const TWO_WEEKS_IN_DAYS = 14;
export const ONE_MONTH_IN_DAYS = 30;
export const TWO_MONTH_IN_DAYS = 60;

export type DateRange = [Dayjs, Dayjs];

export const now = dayjs;

export const nowAsStartOfDay = (): Dayjs => now().startOf("day");

export function datesAreOnSameDay(date1: Dayjs, date2: Dayjs): boolean {
	return date1.isSame(date2, "date");
}

export const dateIsBeforeOrSameAs = (date1: Dayjs, date2: Dayjs): boolean =>
	date1.isBefore(date2, "date") || date1.isSame(date2, "date");

export function addDaysToDate(date: Dayjs, days: number): Dayjs {
	return date.add(days, "days");
}

export function dateToDateDto(date: Dayjs): string {
	return date.format(BACKEND_DATE_FORMAT);
}

export function dateFromDateDto(date: string): Dayjs {
	return dayjs(date, BACKEND_DATE_FORMAT);
}

export function computeEndDate(startDate: Dayjs, durationInWeek: number): Dayjs {
	if (startDate.day() === dayjs.localeData().firstDayOfWeek()) {
		return startDate.add(-1, "day").add(durationInWeek, "weeks");
	}
	return startDate.endOf("week").add(durationInWeek, "weeks");
}

export function computeFlooredDurationInWeek(startDate: Dayjs, endDate: Dayjs): number {
	return Math.max(0, endDate.diff(startDate, "weeks"));
}

export const computeDiffInDays = (startDate: Dayjs, endDate: Dayjs): number =>
	endDate.startOf("day").diff(startDate.startOf("day"), "days");

export const computeDiffBetween = (
	startDate: Dayjs,
	endDate: Dayjs,
): { month: number; weeks: number; days: number } => {
	let month = 0;

	while (dateIsBeforeOrSameAs(startDate.add(month + 1, "months"), endDate)) {
		month += 1;
	}
	const diffInDays = computeDiffInDays(startDate.add(month, "months"), endDate) + 1; //include start date

	return {
		month,
		weeks: Math.floor(diffInDays / 7),
		days: diffInDays % 7,
	};
};

export const dateRangesOverlap = (rangeA: DateRange, rangeB: DateRange): boolean => {
	const [startRangeA, endRangeA] = rangeA;
	const [startRangeB, endRangeB] = rangeB;

	return (
		startRangeA.isBetween(startRangeB, endRangeB, "days", "[]") ||
		endRangeA.isBetween(startRangeB, endRangeB, "days", "[]") ||
		// This last condition is necessary in case range B is completely within range A
		startRangeB.isBetween(startRangeA, endRangeA, "days", "[]")
	);
};

export const daysToMonth = (days: number, calendarMode?: boolean): number => {
	if (calendarMode) {
		const { month } = computeDiffBetween(now(), now().add(days, "days"));
		return month;
	}
	return Math.floor(days / ONE_MONTH_IN_DAYS);
};
